import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPin, Users, Activity, Search, Grid, Map as MapIcon, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapa base vectorial de OpenFreeMap: sin API key; el estilo ya trae su atribución.
const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

// Centro inicial (Zócalo), en [lat, lng] como lo devuelve el backend.
const CDMX_CENTER = [19.4326, -99.1332];

// MapLibre posiciona el elemento externo del marcador con `transform`, así que la animación
// `pulse` va en el círculo interno para no interferir con la posición.
const createMarkerElement = (isActive) => {
  const size = isActive ? 32 : 24;
  const element = document.createElement('div');
  element.className = 'custom-marker-container';
  element.style.cursor = 'pointer';

  const dot = document.createElement('div');
  Object.assign(dot.style, {
    width: `${size}px`,
    height: `${size}px`,
    background: isActive ? '#BC955C' : '#8B1C4A',
    border: `${isActive ? 4 : 3}px solid white`,
    borderRadius: '50%',
    boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.3)',
    animation: isActive ? 'pulse 1.5s infinite' : 'none',
  });
  element.appendChild(dot);

  return element;
};

// El contenido del popup de MapLibre es DOM, no JSX; se arma con textContent para no inyectar HTML.
const createPopupContent = (territory, totalActivities, totalBeneficiaries, onNavigate) => {
  const container = document.createElement('div');
  container.className = 'min-w-[200px]';

  const title = document.createElement('h3');
  title.className = 'font-bold text-[#8B1C4A]';
  title.textContent = territory.name || 'Territorio sin nombre';

  const alcaldia = document.createElement('p');
  alcaldia.className = 'text-sm text-slate-500';
  alcaldia.textContent = territory.alcaldia || 'Alcaldía no disponible';

  const statsBlock = document.createElement('div');
  statsBlock.className = 'mt-2 space-y-1';
  [
    [totalActivities, 'actividades'],
    [totalBeneficiaries.toLocaleString(), 'beneficiarios'],
  ].forEach(([value, label]) => {
    const line = document.createElement('p');
    line.className = 'text-sm';
    const strong = document.createElement('span');
    strong.className = 'font-medium';
    strong.textContent = value;
    line.append(strong, ` ${label}`);
    statsBlock.appendChild(line);
  });

  const link = document.createElement('a');
  const path = `/territorio/${territory.id}`;
  link.href = path;
  link.className = 'inline-block mt-3 text-sm text-[#8B1C4A] font-medium hover:underline';
  link.textContent = 'Ver detalle →';
  link.addEventListener('click', (event) => {
    event.preventDefault();
    onNavigate(path);
  });

  container.append(title, alcaldia, statsBlock, link);
  return container;
};

const getBeneficiariesFromStats = (territoryStats = {}) =>
  Number(
    territoryStats.total_beneficiarios ??
      territoryStats.total_beneficiados ??
      0
  ) || 0;

const getActivitiesFromStats = (territoryStats = {}) =>
  Number(territoryStats.total_activities ?? 0) || 0;

export const Territorios = () => {
  const [territories, setTerritories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('both'); // 'map', 'grid', 'both'
  const [searchQuery, setSearchQuery] = useState('');
  const [alcaldiaFilter, setAlcaldiaFilter] = useState('all');
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [mapCenter, setMapCenter] = useState(CDMX_CENTER);
  const [map, setMap] = useState(null);
  const mapContainerRef = useRef(null);
  const mapCenterRef = useRef(mapCenter);
  const navigate = useNavigate();
  const showMap = !loading && viewMode !== 'grid';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [territoriesRes, statsRes] = await Promise.all([
          axios.get(`${API}/territories`),
          axios.get(`${API}/stats/by-territory`),
        ]);

        setTerritories(Array.isArray(territoriesRes.data) ? territoriesRes.data : []);
        setStats(statsRes.data || {});
      } catch (error) {
        console.error('Error fetching territories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const alcaldias = useMemo(() => {
    return [...new Set(
      territories
        .map((territory) => territory.alcaldia)
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'es'));
  }, [territories]);

  const filteredTerritories = useMemo(() => {
    return territories.filter((territory) => {
      const name = territory.name?.toLowerCase() || '';
      const alcaldia = territory.alcaldia?.toLowerCase() || '';
      const query = searchQuery.toLowerCase();

      const matchesSearch = name.includes(query) || alcaldia.includes(query);
      const matchesAlcaldia =
        alcaldiaFilter === 'all' || territory.alcaldia === alcaldiaFilter;

      return matchesSearch && matchesAlcaldia;
    });
  }, [territories, searchQuery, alcaldiaFilter]);

  const handleTerritoryClick = useCallback((territory) => {
    setSelectedTerritory(territory.id);

    if (
      Number.isFinite(Number(territory.lat)) &&
      Number.isFinite(Number(territory.lng))
    ) {
      setMapCenter([Number(territory.lat), Number(territory.lng)]);
    }
  }, []);

  // Crea el mapa cuando su contenedor está en pantalla (no existe mientras carga ni en vista de lista).
  useEffect(() => {
    if (!showMap || !mapContainerRef.current) return undefined;

    const [lat, lng] = mapCenterRef.current;
    const instance = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: [lng, lat],
      zoom: mapCenterRef.current === CDMX_CENTER ? 11 : 14,
    });
    instance.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
    setMap(instance);

    return () => {
      setMap(null);
      instance.remove();
    };
  }, [showMap]);

  // Marcadores: se rehacen al cambiar el filtro, las estadísticas o el territorio seleccionado.
  useEffect(() => {
    if (!map) return undefined;

    const markers = filteredTerritories
      .filter(
        (territory) =>
          Number.isFinite(Number(territory.lat)) &&
          Number.isFinite(Number(territory.lng))
      )
      .map((territory) => {
        const isActive = selectedTerritory === territory.id;
        const territoryStats = stats[territory.id] || {};
        const popup = new maplibregl.Popup({ offset: isActive ? 16 : 12 }).setDOMContent(
          createPopupContent(
            territory,
            getActivitiesFromStats(territoryStats),
            getBeneficiariesFromStats(territoryStats),
            navigate
          )
        );

        const element = createMarkerElement(isActive);
        element.addEventListener('click', () => handleTerritoryClick(territory));

        return new maplibregl.Marker({ element })
          .setLngLat([Number(territory.lng), Number(territory.lat)])
          .setPopup(popup)
          .addTo(map);
      });

    return () => markers.forEach((marker) => marker.remove());
  }, [map, filteredTerritories, stats, selectedTerritory, navigate, handleTerritoryClick]);

  // Centra el mapa en el territorio elegido desde un marcador o una tarjeta.
  useEffect(() => {
    mapCenterRef.current = mapCenter;
    if (!map || mapCenter === CDMX_CENTER) return;

    const [lat, lng] = mapCenter;
    map.flyTo({ center: [lng, lat], zoom: 14 });
  }, [map, mapCenter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#8B1C4A]/20" />
          <p className="text-slate-600">Cargando territorios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E1]" data-testid="territorios-page">
      <section className="bg-white border-b border-[#D4C5A5]/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
                {territories.length} Territorios de Paz e Igualdad
              </h1>
              <p className="text-slate-600 mt-2">
                Explora las acciones del gobierno en cada territorio estratégico
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-[#8B1C4A] hover:bg-[#70163A]' : ''}
                data-testid="view-grid-btn"
              >
                <Grid className="w-4 h-4" />
              </Button>

              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' ? 'bg-[#8B1C4A] hover:bg-[#70163A]' : ''}
                data-testid="view-map-btn"
              >
                <MapIcon className="w-4 h-4" />
              </Button>

              <Button
                variant={viewMode === 'both' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('both')}
                className={viewMode === 'both' ? 'bg-[#8B1C4A] hover:bg-[#70163A]' : ''}
                data-testid="view-both-btn"
              >
                Ambos
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar territorio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-territory"
              />
            </div>

            <Select value={alcaldiaFilter} onValueChange={setAlcaldiaFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-alcaldia">
                <SelectValue placeholder="Filtrar por Alcaldía" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las Alcaldías</SelectItem>
                {alcaldias.map((alcaldia) => (
                  <SelectItem key={alcaldia} value={alcaldia}>
                    {alcaldia}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid ${viewMode === 'both' ? 'lg:grid-cols-2' : ''} gap-6 lg:gap-8`}>
            {(viewMode === 'map' || viewMode === 'both') && (
              <div className={viewMode === 'map' ? 'col-span-full' : ''}>
                <Card className="overflow-hidden border-none shadow-lg">
                  <div className="h-[320px] sm:h-[420px] lg:h-[600px]" data-testid="territories-map">
                    <div
                      ref={mapContainerRef}
                      style={{ height: '100%', width: '100%' }}
                      className="rounded-lg"
                    />
                  </div>
                </Card>
              </div>
            )}

            {(viewMode === 'grid' || viewMode === 'both') && (
              <div className={viewMode === 'grid' ? 'col-span-full' : ''}>
                <div
                  className={`grid ${
                    viewMode === 'grid'
                      ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                      : 'grid-cols-1'
                  } gap-4`}
                  data-testid="territories-grid"
                >
                  {filteredTerritories.map((territory) => {
                    const territoryStats = stats[territory.id] || {};
                    const isSelected = selectedTerritory === territory.id;
                    const totalActivities = getActivitiesFromStats(territoryStats);
                    const totalBeneficiaries = getBeneficiariesFromStats(territoryStats);

                    return (
                      <Card
                        key={territory.id}
                        className={`cursor-pointer transition-all duration-300 ${
                          isSelected
                            ? 'ring-2 ring-[#BC955C] shadow-lg'
                            : 'hover:shadow-md border border-[#D4C5A5]/30'
                        }`}
                        onClick={() => handleTerritoryClick(territory)}
                        data-testid={`territory-card-${territory.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-slate-800 line-clamp-1">
                                {territory.name || 'Territorio sin nombre'}
                              </h3>
                              <p className="text-sm text-slate-500 mt-0.5">
                                {territory.alcaldia || 'Alcaldía no disponible'}
                              </p>
                            </div>

                            <div className="p-2 bg-[#8B1C4A]/10 rounded-lg">
                              <MapPin className="w-4 h-4 text-[#8B1C4A]" />
                            </div>
                          </div>

                          <div className="flex items-center gap-4 mt-4">
                            <div className="flex items-center gap-1 text-sm">
                              <Users className="w-4 h-4 text-[#BC955C]" />
                              <span className="font-medium">
                                {totalBeneficiaries.toLocaleString()}
                              </span>
                            </div>

                            <div className="flex items-center gap-1 text-sm">
                              <Activity className="w-4 h-4 text-[#235B4E]" />
                              <span className="font-medium">{totalActivities}</span>
                            </div>
                          </div>

                          <Link
                            to={`/territorio/${territory.id}`}
                            className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 text-sm text-[#8B1C4A] font-medium hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Ver actividades
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {filteredTerritories.length === 0 && (
                  <div className="text-center py-12">
                    <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No se encontraron territorios</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Territorios;